import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, Receipt, ChevronDown, ChevronUp, Tag, RefreshCw
} from 'lucide-react';
import api from '../../services/api';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const emptyRow = () => ({
  InvoiceID:             null,
  RevenueTypeID:         null,
  revenuetypecode:       '',
  revenuetypename:       '',
  revenuetypegroup:      '',
  ProvisionCurrencyID:   null,
  ProvisionExchangeRate: 1,
  ProvisionForeign:      0,
  ProvisionHome:         0,
  ProvisionRate:         0,
  SalesCurrencyID:       null,
  SalesExchangeRate:     1,
  SalesForeign:          0,
  SalesHome:             0,
  SalesRate:             0,
  Cost:                  0,
  SupplierID:            null,
  RevenueCode:           '',
  Quantity:              1,
  UnitID:                null,
  TaxID:                 null,
  TaxPer:                0,
  TaxAmt:                0,
  TaxAmtF:               0,
  AmtInWords:            '',
});

const fmt2 = (n) => (parseFloat(n) || 0).toFixed(2);

const InvoiceTab = ({
  data,
  masterData,
  jobDate,
  invoiceNo,
  invoiceDate,
  onChange,
  onTaxChange,
  onInvoiceNoChange,
  onInvoiceDateChange,
}) => {
  const revenueTypes = masterData.revenuetype || [];
  const suppliers    = masterData.suppliers   || [];
  const currencies   = masterData.currencies  || [];
  const units        = masterData.units       || [];

  const [rows,         setRows]         = useState(data?.length ? data : [emptyRow()]);
  const [generatingNo, setGeneratingNo] = useState(false);
  const [expandedRow,  setExpandedRow]  = useState(null);

  useEffect(() => { if (data?.length) setRows(data); }, [data]);

  const push = (updated) => {
    setRows(updated);
    onChange(updated);
    const totalTax = updated.reduce((s, r) => s + (parseFloat(r.TaxAmt) || 0), 0);
    onTaxChange?.(totalTax);
  };

  const addRow    = () => push([...rows, emptyRow()]);
  const removeRow = (idx) => push(rows.length === 1 ? [emptyRow()] : rows.filter((_, i) => i !== idx));

  const handleField = (idx, field, value) => {
    const updated = rows.map((r, i) => {
      if (i !== idx) return r;
      const row = { ...r, [field]: value };

      if (field === 'RevenueTypeID') {
        const rt = revenueTypes.find(x => String(x.id) === String(value));
        if (rt) {
          row.revenuetypecode   = rt.revenuetypecode  || '';
          row.revenuetypename   = rt.revenuetypename  || '';
          row.revenuetypegroup  = rt.revenuetypegroup || '';
          row.RevenueCode       = rt.revenuetypecode  || '';
          row.accountheadid     = rt.accountheadid     || null;
          row.costaccountheadid = rt.costaccountheadid || null;
          const taxRate = parseFloat(rt.taxrate) || 0;
          row.TaxPer  = taxRate;
          row.TaxAmt  = parseFloat(((parseFloat(row.SalesHome)    || 0) * taxRate / 100).toFixed(2));
          row.TaxAmtF = parseFloat(((parseFloat(row.SalesForeign) || 0) * taxRate / 100).toFixed(2));
        }
      }
      if (field === 'SalesHome' || field === 'TaxPer') {
        const sh = parseFloat(field === 'SalesHome' ? value : row.SalesHome) || 0;
        const tp = parseFloat(field === 'TaxPer'    ? value : row.TaxPer)    || 0;
        row.TaxAmt = parseFloat((sh * tp / 100).toFixed(2));
      }
      if (field === 'SalesForeign' || field === 'TaxPer') {
        const sf = parseFloat(field === 'SalesForeign' ? value : row.SalesForeign) || 0;
        const tp = parseFloat(field === 'TaxPer'       ? value : row.TaxPer)       || 0;
        row.TaxAmtF = parseFloat((sf * tp / 100).toFixed(2));
      }
      return row;
    });
    push(updated);
  };

  const generateInvoiceNo = async () => {
    setGeneratingNo(true);
    try {
      const res = await api.get('/JobGeneration.php?action=next-invoice-no');
      if (res.data?.success) {
        onInvoiceNoChange?.(res.data.data.invoiceNo);
        if (!invoiceDate) onInvoiceDateChange?.(new Date().toISOString().slice(0, 10));
      }
    } catch (e) { console.error('Invoice No gen failed:', e.message); }
    finally { setGeneratingNo(false); }
  };

  const totals = rows.reduce(
    (acc, r) => ({
      salesHome: acc.salesHome + (parseFloat(r.SalesHome)    || 0),
      provision: acc.provision + (parseFloat(r.ProvisionHome) || 0),
      tax:       acc.tax       + (parseFloat(r.TaxAmt)        || 0),
      cost:      acc.cost      + (parseFloat(r.Cost)          || 0),
    }),
    { salesHome: 0, provision: 0, tax: 0, cost: 0 }
  );

  const rtGroups = revenueTypes.reduce((acc, rt) => {
    const grp = rt.revenuetypegroup || 'Other';
    if (!acc[grp]) acc[grp] = [];
    acc[grp].push(rt);
    return acc;
  }, {});

  const hasRows = rows.some(r => r.RevenueTypeID);

  // shared cell/input styles — Tailwind strings
  const cellInp = (w, right) =>
    `border border-[#CBD5E1] rounded px-1.5 py-1 text-xs font-mono bg-white outline-none focus:border-[#2563EB] ${right ? 'text-right' : ''}`
    + (w ? ` w-[${w}]` : ' w-full');
  const detailInp  = `border border-[#CBD5E1] rounded px-2 py-1 text-xs font-mono bg-white outline-none focus:border-[#2563EB] text-right min-w-[90px]`;
  const detailSel  = `border border-[#CBD5E1] rounded px-1.5 py-1 text-xs bg-white outline-none cursor-pointer min-w-[110px]`;
  const detailLbl  = 'text-[10px] font-bold text-gray-500 uppercase tracking-widest';
  const thCls      = 'px-2.5 py-2 text-left text-[11px] font-bold tracking-wide whitespace-nowrap text-white border-r border-white/20 last:border-r-0';
  const tdCls      = (align) => `px-2 py-1.5 border-b border-gray-100 align-middle ${align === 'right' ? 'text-right' : ''}`;

  return (
    <div className="space-y-6">

      {/* ── Invoice No / Date ── */}
      <section>
        <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-gray-200">
          <div className="w-1 h-5 rounded-full" style={{ background: PRIMARY }} />
          <Receipt size={15} style={{ color: PRIMARY }} />
          <h3 className="text-base font-semibold text-gray-800 m-0">Invoice Reference</h3>
        </div>
        <div className="flex gap-4 flex-wrap items-end">
          <div className="flex flex-col gap-1.5 min-w-[240px]">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Invoice No</label>
            <div className="flex gap-1.5">
              <input type="text" value={invoiceNo || ''}
                onChange={e => onInvoiceNoChange?.(e.target.value)}
                placeholder="e.g. INV-000001"
                className={`flex-1 px-3 py-2 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#2563EB] font-mono ${invoiceNo ? 'font-bold' : ''}`} />
              <button type="button" onClick={generateInvoiceNo}
                disabled={generatingNo || !hasRows || !!invoiceNo}
                title={invoiceNo ? 'Invoice number already assigned' : !hasRows ? 'Add revenue lines first' : 'Generate next invoice number'}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-md border transition-all duration-150 ${
                  !hasRows || !!invoiceNo
                    ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
                style={!hasRows || !!invoiceNo ? {} : { borderColor: '#93C5FD', background: '#EFF6FF', color: '#1D4ED8' }}
                onMouseEnter={(e) => { if (!(!hasRows || !!invoiceNo)) e.currentTarget.style.background = '#DBEAFE'; }}
                onMouseLeave={(e) => { if (!(!hasRows || !!invoiceNo)) e.currentTarget.style.background = '#EFF6FF'; }}>
                {generatingNo
                  ? <RefreshCw size={12} className="animate-spin" />
                  : <Tag size={12} />}
                Generate
              </button>
            </div>
            {!hasRows && !invoiceNo && <small className="text-[11px] text-gray-400">Add revenue lines below first</small>}
            {invoiceNo && <small className="text-[11px]" style={{ color: '#16A34A' }}>✓ Invoice number assigned — clear field to regenerate</small>}
          </div>
          <div className="flex flex-col gap-1.5 min-w-[160px]">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Invoice Date</label>
            <input type="date" value={invoiceDate || ''}
              onChange={e => onInvoiceDateChange?.(e.target.value)}
              className="px-3 py-2 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#2563EB]" />
          </div>
        </div>
      </section>

      {/* ── Revenue / Cost Lines ── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 pb-2 border-b-2 border-gray-200 flex-1 mr-4">
            <div className="w-1 h-5 rounded-full" style={{ background: PRIMARY }} />
            <h3 className="text-base font-semibold text-gray-800 m-0">Revenue / Cost Lines</h3>
          </div>
          <button type="button" onClick={addRow}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white border-0 rounded-lg cursor-pointer hover:-translate-y-0.5 transition-all duration-150"
            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
            <Plus size={13} /> Add Line
          </button>
        </div>

        <div className="overflow-x-auto border border-[#E2E8F0] rounded-lg">
          <table className="w-full border-collapse text-xs" style={{ minWidth: 900 }}>
            <thead className="sticky top-0 z-10"
              style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
              <tr>
                <th className={thCls} style={{ width: 28 }}>#</th>
                <th className={thCls} style={{ minWidth: 200 }}>Revenue Type *</th>
                <th className={thCls} style={{ width: 90 }}>Code</th>
                <th className={thCls + ' text-right'} style={{ width: 70 }}>Qty</th>
                <th className={thCls + ' text-right'} style={{ width: 100 }}>Sales (Home)</th>
                <th className={thCls + ' text-right'} style={{ width: 90 }}>Provision</th>
                <th className={thCls + ' text-right'} style={{ width: 60 }}>Tax %</th>
                <th className={thCls + ' text-right'} style={{ width: 80 }}>Tax Amt</th>
                <th className={thCls + ' text-right'} style={{ width: 80 }}>Cost</th>
                <th className={thCls + ' text-right'} style={{ width: 80 }}>Profit</th>
                <th className={thCls} style={{ width: 120 }}>Supplier</th>
                <th className={thCls} style={{ width: 30 }} />
                <th className={thCls} style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const expanded = expandedRow === idx;
                const selRt    = revenueTypes.find(x => String(x.id) === String(row.RevenueTypeID));
                const profit   = (parseFloat(row.SalesHome) || 0) - (parseFloat(row.Cost) || 0);
                const rowBg    = idx % 2 === 0 ? '#fff' : '#EFF6FF';

                return (
                  <React.Fragment key={idx}>
                    <tr style={{ background: rowBg }}>
                      <td className={tdCls('center') + ' font-bold'} style={{ color: PRIMARY }}>{idx + 1}</td>

                      {/* Revenue Type */}
                      <td className={tdCls()}>
                        <select value={row.RevenueTypeID || ''}
                          onChange={e => handleField(idx, 'RevenueTypeID', e.target.value || null)}
                          className="w-full text-xs px-1.5 py-1 border border-[#CBD5E1] rounded outline-none cursor-pointer"
                          style={{ background: row.RevenueTypeID ? '#EFF6FF' : '#fff' }}>
                          <option value="">— Select Revenue Type —</option>
                          {Object.entries(rtGroups).map(([grp, grpItems]) => (
                            <optgroup key={grp} label={grp}>
                              {grpItems.map(rt => (
                                <option key={rt.id} value={rt.id}>
                                  {rt.revenuetypename}{rt.revenuetypecode ? ` [${rt.revenuetypecode}]` : ''}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {selRt?.revenuetypegroup && (
                          <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full mt-1" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                            {selRt.revenuetypegroup}
                          </span>
                        )}
                      </td>

                      {/* Code */}
                      <td className={tdCls()}>
                        <input type="text" value={row.RevenueCode || ''} placeholder="code"
                          onChange={e => handleField(idx, 'RevenueCode', e.target.value)}
                          className="w-full border border-[#CBD5E1] rounded px-1.5 py-1 text-xs outline-none focus:border-[#2563EB]" />
                      </td>

                      {/* Qty */}
                      <td className={tdCls('right')}>
                        <input type="number" value={row.Quantity || 1} min={0} step={1}
                          onChange={e => handleField(idx, 'Quantity', e.target.value)}
                          className="w-14 border border-[#CBD5E1] rounded px-1.5 py-1 text-xs font-mono text-right outline-none focus:border-[#2563EB]" />
                      </td>

                      {/* Sales Home */}
                      <td className={tdCls('right')}>
                        <input type="number" value={row.SalesHome || 0} min={0} step={0.01}
                          onChange={e => handleField(idx, 'SalesHome', e.target.value)}
                          className="w-[88px] border border-[#CBD5E1] rounded px-1.5 py-1 text-xs font-mono text-right outline-none focus:border-[#2563EB]" />
                      </td>

                      {/* Provision */}
                      <td className={tdCls('right')}>
                        <input type="number" value={row.ProvisionHome || 0} min={0} step={0.01}
                          onChange={e => handleField(idx, 'ProvisionHome', e.target.value)}
                          className="w-[76px] border border-[#CBD5E1] rounded px-1.5 py-1 text-xs font-mono text-right outline-none focus:border-[#2563EB]" />
                      </td>

                      {/* Tax % */}
                      <td className={tdCls('right')}>
                        <input type="number" value={row.TaxPer || 0} min={0} max={100} step={0.01}
                          onChange={e => handleField(idx, 'TaxPer', e.target.value)}
                          className="w-[52px] border border-[#CBD5E1] rounded px-1.5 py-1 text-xs font-mono text-right outline-none focus:border-[#2563EB]" />
                      </td>

                      {/* Tax Amt — readonly */}
                      <td className={tdCls('right')}>
                        <input type="number" value={row.TaxAmt || 0} readOnly
                          className="w-[72px] border border-gray-200 rounded px-1.5 py-1 text-xs font-mono text-right bg-gray-100 text-gray-500 outline-none" />
                      </td>

                      {/* Cost */}
                      <td className={tdCls('right')}>
                        <input type="number" value={row.Cost || 0} min={0} step={0.01}
                          onChange={e => handleField(idx, 'Cost', e.target.value)}
                          className="w-[74px] border border-[#CBD5E1] rounded px-1.5 py-1 text-xs font-mono text-right outline-none focus:border-[#2563EB]" />
                      </td>

                      {/* Profit — readonly */}
                      <td className={tdCls('right')}>
                        <input type="number" value={profit.toFixed(2)} readOnly
                          className="w-[74px] rounded px-1.5 py-1 text-xs font-mono font-bold text-right outline-none"
                          style={profit >= 0
                            ? { border: '1px solid #BBF7D0', background: '#F0FDF4', color: '#15803D' }
                            : { border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626' }} />
                      </td>

                      {/* Supplier */}
                      <td className={tdCls()}>
                        <select value={row.SupplierID || ''}
                          onChange={e => handleField(idx, 'SupplierID', e.target.value || null)}
                          className="w-full text-xs px-1.5 py-1 border border-[#CBD5E1] rounded outline-none cursor-pointer">
                          <option value="">— None —</option>
                          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </td>

                      {/* Expand */}
                      <td className={tdCls('center')}>
                        <button type="button" onClick={() => setExpandedRow(expanded ? null : idx)}
                          title="Currency & detail fields"
                          className="p-1 border rounded cursor-pointer transition-colors duration-150"
                          style={expanded
                            ? { borderColor: '#93C5FD', background: '#DBEAFE', color: '#1D4ED8' }
                            : { borderColor: '#E2E8F0', background: '#fff', color: '#6B7280' }}>
                          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      </td>

                      {/* Delete */}
                      <td className={tdCls('center')}>
                        <button type="button" onClick={() => removeRow(idx)}
                          className="p-1 border rounded cursor-pointer transition-colors duration-150"
                          style={{ borderColor: '#FECACA', background: '#FEE2E2', color: '#DC2626' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#FECACA'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#FEE2E2'; }}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {expanded && (
                      <tr style={{ background: '#EFF6FF', borderBottom: '2px solid #93C5FD' }}>
                        <td colSpan={13} className="px-4 py-3">
                          <div className="flex gap-4 flex-wrap">
                            {[
                              ['Prov. Currency',   'ProvisionCurrencyID',   'cur'],
                              ['Prov. Exch. Rate', 'ProvisionExchangeRate', 'num'],
                              ['Prov. Foreign',    'ProvisionForeign',      'num'],
                              ['Sales Currency',   'SalesCurrencyID',       'cur'],
                              ['Sales Exch. Rate', 'SalesExchangeRate',     'num'],
                              ['Sales Foreign',    'SalesForeign',          'num'],
                              ['Unit',             'UnitID',                'unit'],
                            ].map(([label, field, type]) => (
                              <div key={field} className="flex flex-col gap-1">
                                <label className={detailLbl}>{label}</label>
                                {type === 'cur' ? (
                                  <select value={row[field] || ''}
                                    onChange={e => handleField(idx, field, e.target.value || null)}
                                    className={detailSel}>
                                    <option value="">— Home —</option>
                                    {currencies.map(c => <option key={c.id} value={c.id}>{c.name} {c.Symbol || ''}</option>)}
                                  </select>
                                ) : type === 'unit' ? (
                                  <select value={row[field] || ''}
                                    onChange={e => handleField(idx, field, e.target.value || null)}
                                    className={detailSel}>
                                    <option value="">— None —</option>
                                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                  </select>
                                ) : (
                                  <input type="number" value={row[field] || 0} step={0.01} min={0}
                                    onChange={e => handleField(idx, field, e.target.value)}
                                    className={detailInp} />
                                )}
                              </div>
                            ))}
                            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                              <label className={detailLbl}>Amount in Words</label>
                              <input type="text" value={row.AmtInWords || ''}
                                onChange={e => handleField(idx, 'AmtInWords', e.target.value)}
                                placeholder="Optional"
                                className="border border-[#CBD5E1] rounded px-2 py-1 text-xs bg-white outline-none focus:border-[#2563EB] min-w-[200px]" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>

            {hasRows && (() => {
              const profit = totals.salesHome - totals.cost;
              return (
                <tfoot>
                  <tr className="font-bold" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, color: '#fff' }}>
                    <td colSpan={4} className="px-2.5 py-2 text-right text-[11px] whitespace-nowrap">
                      {rows.filter(r => r.RevenueTypeID).length} line(s) — totals:
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[13px]">{fmt2(totals.salesHome)}</td>
                    <td className="px-2 py-2 text-right font-mono text-[13px]">{fmt2(totals.provision)}</td>
                    <td />
                    <td className="px-2 py-2 text-right font-mono text-[13px]">{fmt2(totals.tax)}</td>
                    <td className="px-2 py-2 text-right font-mono text-[13px]">{fmt2(totals.cost)}</td>
                    <td className="px-2 py-2 text-right font-mono text-[13px]"
                        style={{ color: profit >= 0 ? '#bbf7d0' : '#fecaca' }}>
                      {fmt2(profit)}
                    </td>
                    <td className="px-2 py-2 text-right text-[12px] whitespace-nowrap">
                      Grand: {fmt2(totals.salesHome + totals.tax)}
                    </td>
                    <td /><td />
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>

        {revenueTypes.length === 0 && (
          <div className="mt-3 px-4 py-3 rounded-lg text-sm" style={{ background: '#FFFBEB', border: '1px dashed #F59E0B', color: '#B45309' }}>
            ⚠️ No revenue types found for this company. Go to <strong>Revenue Type Master</strong> to add them first.
          </div>
        )}
      </section>
    </div>
  );
};

export default InvoiceTab;